// ============================================================================
// PromptModal.jsx — Mobile-friendly replacement for window.prompt()
// Supports single-field or multi-field input, defaults, validation, ESC to cancel
// ============================================================================

import { useState, useEffect, useRef } from 'react'

/**
 * Usage:
 *   <PromptModal
 *     open={showPrompt}
 *     title="Pin title"
 *     description="This will appear on your Pinterest pin"
 *     fields={[
 *       { name: 'title', label: 'Title', placeholder: 'My craft', default: 'My craft' },
 *       { name: 'description', label: 'Description', multiline: true, maxLength: 500 }
 *     ]}
 *     onSubmit={(values) => { ... }}
 *     onCancel={() => setShowPrompt(false)}
 *   />
 *
 * OR imperatively via the helper (see usePromptModal below)
 */
export default function PromptModal({
  open,
  title,
  description,
  fields = [{ name: 'value', label: '', placeholder: '', default: '' }],
  submitLabel = 'OK',
  cancelLabel = 'Cancel',
  onSubmit,
  onCancel,
}) {
  const [values, setValues] = useState({})
  const firstInputRef = useRef(null)

  useEffect(() => {
    if (open) {
      const initial = {}
      for (const f of fields) initial[f.name] = f.default || ''
      setValues(initial)
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  const handleSubmit = (e) => {
    e?.preventDefault()
    onSubmit?.(values)
  }

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full border-t sm:border border-slate-200 dark:border-slate-700 overflow-hidden mb-safe"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
            {title && <h2 className="text-base font-bold text-slate-900 dark:text-white">{title}</h2>}
            {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
          </div>

          {/* Fields */}
          <div className="p-5 space-y-4">
            {fields.map((f, i) => (
              <div key={f.name}>
                {f.label && (
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
                    {f.label}
                    {f.maxLength && (
                      <span className="ml-1 text-slate-400 font-normal">
                        ({(values[f.name] || '').length}/{f.maxLength})
                      </span>
                    )}
                  </label>
                )}
                {f.multiline ? (
                  <textarea
                    ref={i === 0 ? firstInputRef : null}
                    value={values[f.name] || ''}
                    onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                    placeholder={f.placeholder}
                    maxLength={f.maxLength}
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-zenna-violet resize-none"
                  />
                ) : (
                  <input
                    ref={i === 0 ? firstInputRef : null}
                    type={f.type || 'text'}
                    value={values[f.name] || ''}
                    onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                    placeholder={f.placeholder}
                    maxLength={f.maxLength}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-zenna-violet"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex gap-2 justify-end bg-slate-50 dark:bg-slate-900/50">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition min-h-[44px]"
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white text-sm font-semibold hover:opacity-90 transition min-h-[44px]"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
